/**
 * MCP submissions API client — OTF and RTF (TRUS-149 / TRUS-366).
 *
 * The outbound half of the MIF work. Where mifImport.ts loads what a health plan
 * sends us, this sends the answer back: a versioned spec per plan, a dry run you
 * can read before anything leaves, and a kept copy of the bytes as transmitted.
 *
 * Everything goes through the shared `apiClient` for the same reasons spelled out
 * in mifImport.ts — the `auth_token` key, the `X-CBO-ID` header every endpoint
 * scopes on, and the product-aware base URL. No hand-built headers here.
 */

import { apiClient } from '@/services/apiClient';

const BASE = '/api/mif/submissions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** otf = outreach tracking, rtf = the enrolment/status return. */
export type SubmissionType = 'otf' | 'rtf' | 'other';

/**
 * `derived` means the spec was built from the inbound pack's organization-owned
 * columns rather than typed in — the round trip closes by construction, so what
 * we send and what the loader protects cannot drift apart.
 */
export type LayoutOrigin = 'shipped' | 'derived' | 'authored';

export type LayoutStatus = 'draft' | 'published' | 'retired';

export type BatchStatus =
  | 'draft'
  | 'validated'
  | 'submitted'
  | 'accepted'
  | 'rejected'
  | 'superseded'
  | 'failed';

export interface SubmissionLayout {
  id: number;
  cbo_id: number | null;
  plan_key: string;
  plan_name: string;
  layout_version: string;
  submission_type: SubmissionType;
  origin: LayoutOrigin;
  status: LayoutStatus;
  source_note?: string | null;
  derived_from_pack_id?: number | null;
  cadence: string;
  due_day_of_month?: number | null;
  delimiter: string;
  include_header: boolean;
  created_at: string;
  published_at?: string | null;
  column_count: number;
  /** Files already sent under this spec. A published layout with sends behind it
   *  is history the organization has to be able to point at. */
  sent_count: number;
}

export interface SubmissionColumn {
  id: number;
  column_name: string;
  ordinal: number;
  /** Where the value comes from: a member field, a literal, or the run itself. */
  source_kind: 'field' | 'constant' | 'file_meta' | 'computed';
  source_field?: string | null;
  source_expression?: string | null;
  constant_value?: string | null;
  /** The inverse of however the loader reads this column inbound. */
  format: string;
  format_config?: Record<string, unknown> | null;
  required: boolean;
  max_length?: number | null;
  allowed_values?: string[] | null;
  rationale?: string | null;
}

export interface SubmissionBatch {
  id: number;
  submission_type: SubmissionType;
  reporting_period: string | null;
  status: BatchStatus;
  members_considered: number;
  rows_included: number;
  rows_excluded: number;
  file_name: string | null;
  file_sha256: string | null;
  file_bytes: number | null;
  generated_at: string;
  submitted_at?: string | null;
  transmission_ref?: string | null;
  rejection_note?: string | null;
  plan_name?: string | null;
  plan_key?: string | null;
  layout_version?: string | null;
  generated_by_name?: string | null;
  submitted_by_name?: string | null;
}

export interface SubmissionIssue {
  column: string | null;
  severity: 'error' | 'warning';
  message: string;
}

export interface SubmissionRow {
  row_number: number | null;
  member_id: number | null;
  member_name: string | null;
  included: boolean;
  /** Why this member is not in the file. Recorded rather than re-derived, because
   *  "why is she missing" is the question that follows every submission. */
  exclusion_reason: string | null;
  issues: SubmissionIssue[] | null;
  row_values: Record<string, string> | null;
}

export interface BuildResult {
  batch_id: number;
  status: BatchStatus;
  file_name: string;
  file_sha256: string;
  members_considered: number;
  rows_included: number;
  rows_excluded: number;
  errors: number;
  warnings: number;
  exclusions: number;
  preview: string[];
  message: string;
}

export interface DeriveResult {
  layout_id: number;
  created: boolean;
  submission_type: SubmissionType;
  plan_key: string;
  layout_version: string;
  status: LayoutStatus;
  columns: number;
  derived_from_pack_id: number;
  message: string;
}

export interface SubmitResult {
  batch_id: number;
  status: BatchStatus;
  rows_sent: number;
  file_sha256: string;
  members_stamped: number;
  message: string;
}

// ---------------------------------------------------------------------------
// Display vocabulary
// ---------------------------------------------------------------------------

export const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  otf: 'Outreach Tracking (OTF)',
  rtf: 'Return Tracking (RTF)',
  other: 'Other submission',
};

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  draft: 'Needs attention',
  validated: 'Ready to send',
  submitted: 'Sent',
  accepted: 'Accepted by plan',
  rejected: 'Rejected by plan',
  superseded: 'Replaced',
  failed: 'Failed',
};

export const SOURCE_KIND_LABELS: Record<SubmissionColumn['source_kind'], string> = {
  field: 'Member field',
  constant: 'Same on every row',
  file_meta: 'From this run',
  computed: 'Calculated',
};

// ---------------------------------------------------------------------------
// Layouts
// ---------------------------------------------------------------------------

export const listSubmissionLayouts = async (
  submissionType?: SubmissionType
): Promise<{ layouts: SubmissionLayout[] }> => {
  const { data } = await apiClient.get(`${BASE}/layouts`, {
    params: submissionType ? { submission_type: submissionType } : undefined,
  });
  return data;
};

export const getSubmissionLayout = async (
  layoutId: number
): Promise<{ layout: SubmissionLayout; columns: SubmissionColumn[] }> => {
  const { data } = await apiClient.get(`${BASE}/layouts/${layoutId}`);
  return data;
};

/**
 * Build the outbound spec from an inbound rule pack.
 *
 * Only the columns that pack marks as owned by this organization are taken — they
 * are, by definition, the ones it refuses to let the plan overwrite because the
 * plan echoes them from our last submission.
 */
export const deriveSubmissionLayout = async (body: {
  pack_id: number;
  submission_type: SubmissionType;
  layout_version: string;
}): Promise<DeriveResult> => {
  const { data } = await apiClient.post(`${BASE}/layouts/derive-from-pack`, body);
  return data;
};

export const setSubmissionLayoutStatus = async (
  layoutId: number,
  status: LayoutStatus
): Promise<{ layout_id: number; status: LayoutStatus }> => {
  const { data } = await apiClient.post(`${BASE}/layouts/${layoutId}/status`, { status });
  return data;
};

// ---------------------------------------------------------------------------
// Building and sending
// ---------------------------------------------------------------------------

/** Builds the file and everything needed to review it. Sends nothing. */
export const buildSubmission = async (body: {
  layout_id: number;
  reporting_period: string;
  period_start?: string | null;
  period_end?: string | null;
}): Promise<BuildResult> => {
  const { data } = await apiClient.post(`${BASE}/batches`, body);
  return data;
};

export const listSubmissionBatches = async (
  limit = 50
): Promise<{ batches: SubmissionBatch[] }> => {
  const { data } = await apiClient.get(`${BASE}/batches`, { params: { limit } });
  return data;
};

export const listSubmissionRows = async (
  batchId: number,
  included?: boolean
): Promise<{ rows: SubmissionRow[] }> => {
  const { data } = await apiClient.get(`${BASE}/batches/${batchId}/rows`, {
    params: included === undefined ? undefined : { included },
  });
  return data;
};

/**
 * The file exactly as built and, once sent, exactly as sent — served from the
 * stored bytes rather than regenerated. Member data moves, so a regeneration
 * would differ and could not answer "what did we send you".
 */
export const getSubmissionFile = async (batchId: number): Promise<string> => {
  const { data } = await apiClient.get(`${BASE}/batches/${batchId}/file`, {
    responseType: 'text',
  });
  return typeof data === 'string' ? data : String(data);
};

export const submitSubmission = async (
  batchId: number,
  transmissionRef?: string
): Promise<SubmitResult> => {
  const { data } = await apiClient.post(`${BASE}/batches/${batchId}/submit`, {
    transmission_ref: transmissionRef || null,
  });
  return data;
};

export const recordSubmissionDecision = async (
  batchId: number,
  outcome: 'accepted' | 'rejected',
  note?: string
): Promise<{ batch_id: number; status: BatchStatus }> => {
  const { data } = await apiClient.post(`${BASE}/batches/${batchId}/decision`, {
    outcome,
    note: note || null,
  });
  return data;
};
